import { useRef, useState } from "react";
import { etiquetaNumClausulaPorIndice, MAX_ENCARGO_CLAUSULAS } from "../utils/encargoClausulasVisual";

const MARCADORES = [
  { token: "{{PRECIO_FMT}}", label: "Precio" },
  { token: "{{MUEBLES_SI_NO}}", label: "Muebles Sí/No" },
  { token: "{{MUEBLES_ANEXO}}", label: "Anexo muebles" },
  { token: "{{PLAZO_ESCRITURA}}", label: "Plazo escritura" },
  { token: "{{EXCLUSIVA_SI_NO}}", label: "Exclusiva Sí/No" },
  { token: "{{EXCLUSIVA_COMPLEMENTO}}", label: "Texto exclusiva" },
];

/**
 * Editor tipo documento: una caja por cláusula (sin editar JSON a mano).
 */
export default function EncargoClausulasEditor({ filas, onChangeFilas, c, theme }) {
  const taRefs = useRef([]);
  const [focusIdx, setFocusIdx] = useState(0);

  function setRef(i, el) {
    taRefs.current[i] = el;
  }

  function updateRow(i, patch) {
    const next = filas.map((row, j) => (j === i ? { ...row, ...patch } : row));
    onChangeFilas(next);
  }

  function anadirClausula() {
    if (filas.length >= MAX_ENCARGO_CLAUSULAS) return;
    const num = etiquetaNumClausulaPorIndice(filas.length);
    onChangeFilas([...filas, { num, texto: "" }]);
    setFocusIdx(filas.length);
  }

  function quitarClausula(i) {
    if (filas.length <= 1) return;
    const next = filas.filter((_, j) => j !== i);
    onChangeFilas(next);
    setFocusIdx(Math.min(focusIdx, Math.max(0, next.length - 1)));
  }

  function insertMarker(token) {
    const i = Math.min(Math.max(0, focusIdx), Math.max(0, filas.length - 1));
    const el = taRefs.current[i];
    const texto = filas[i]?.texto ?? "";
    if (!el) {
      updateRow(i, { texto: texto + token });
      return;
    }
    const start = typeof el.selectionStart === "number" ? el.selectionStart : texto.length;
    const end = typeof el.selectionEnd === "number" ? el.selectionEnd : start;
    const newTexto = texto.slice(0, start) + token + texto.slice(end);
    updateRow(i, { texto: newTexto });
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch {
        /* */
      }
    });
  }

  const paperBg = theme.name === "dark" ? "#0f172a" : "#ffffff";
  const paperBorder = theme.name === "dark" ? "#334155" : "#e2e8f0";
  const ink = theme.name === "dark" ? "#e2e8f0" : "#1e293b";

  return (
    <div>
      <div
        style={{
          marginBottom: 14,
          padding: "10px 12px",
          borderRadius: 8,
          background: theme.name === "dark" ? "rgba(59,130,246,0.12)" : "#eff6ff",
          border: `1px solid ${theme.name === "dark" ? "rgba(59,130,246,0.35)" : "#bfdbfe"}`,
          fontSize: 12,
          color: c.textPrimary,
          lineHeight: 1.55,
        }}
      >
        Edita el texto de cada cláusula como en un documento. Puedes <strong>añadir o quitar</strong> cláusulas abajo; la numeración
        romana (I.-, II.-, …) la puedes cambiar a mano en cada celda. Coloca el cursor en el párrafo y pulsa un{" "}
        <strong>marcador</strong> para insertarlo; al <strong>Guardar</strong> el CRM convierte todo a JSON para el PDF.
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: c.textMuted }}>Insertar marcador</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
        {MARCADORES.map((m) => (
          <button
            key={m.token}
            type="button"
            onClick={() => insertMarker(m.token)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              background: c.bgApp,
              color: c.textPrimary,
              fontSize: 12,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div
        style={{
          background: paperBg,
          border: `1px solid ${paperBorder}`,
          borderRadius: 4,
          boxShadow: theme.name === "dark" ? "0 1px 3px rgba(0,0,0,0.4)" : "0 4px 24px rgba(15,23,42,0.08)",
          padding: "36px 11%",
          maxWidth: 820,
          margin: "0 auto",
        }}
      >
        {filas.map((row, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              marginBottom: i < filas.length - 1 ? 22 : 0,
            }}
          >
            <input
              type="text"
              value={row.num}
              onChange={(e) => updateRow(i, { num: e.target.value })}
              aria-label={`Numeración cláusula ${i + 1}`}
              style={{
                width: 72,
                minWidth: 72,
                flexShrink: 0,
                marginTop: 2,
                padding: "6px 4px",
                textAlign: "right",
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "Georgia, 'Times New Roman', Times, serif",
                border: `1px solid ${paperBorder}`,
                borderRadius: 6,
                background: theme.name === "dark" ? "#1e293b" : "#f8fafc",
                color: ink,
              }}
            />
            <textarea
              ref={(el) => setRef(i, el)}
              value={row.texto}
              onChange={(e) => updateRow(i, { texto: e.target.value })}
              onFocus={() => setFocusIdx(i)}
              rows={Math.min(14, Math.max(3, Math.ceil(row.texto.length / 72) + 1))}
              spellCheck="true"
              style={{
                flex: 1,
                minWidth: 0,
                padding: "8px 10px",
                fontSize: 15,
                lineHeight: 1.65,
                fontFamily: "Georgia, 'Times New Roman', Times, serif",
                color: ink,
                border: `1px solid ${paperBorder}`,
                borderRadius: 6,
                background: theme.name === "dark" ? "#1e293b" : "#fafafa",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            {filas.length > 1 && (
              <button
                type="button"
                title="Quitar esta cláusula"
                onClick={() => quitarClausula(i)}
                style={{
                  flexShrink: 0,
                  marginTop: 4,
                  width: 32,
                  height: 32,
                  padding: 0,
                  borderRadius: 8,
                  border: `1px solid ${paperBorder}`,
                  background: theme.name === "dark" ? "#334155" : "#f1f5f9",
                  color: c.textMuted,
                  cursor: "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
          marginTop: 20,
          maxWidth: 820,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <button
          type="button"
          onClick={anadirClausula}
          disabled={filas.length >= MAX_ENCARGO_CLAUSULAS}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            border: `1px solid ${c.border}`,
            background: c.accentBlue,
            color: "#fff",
            fontWeight: 600,
            fontSize: 14,
            cursor: filas.length >= MAX_ENCARGO_CLAUSULAS ? "not-allowed" : "pointer",
            opacity: filas.length >= MAX_ENCARGO_CLAUSULAS ? 0.55 : 1,
          }}
        >
          + Añadir cláusula
        </button>
        <span style={{ fontSize: 12, color: c.textMuted }}>
          {filas.length} / {MAX_ENCARGO_CLAUSULAS} cláusulas
        </span>
      </div>
    </div>
  );
}
